// TODO: build 2FA
// import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Tagline} from "@khanacademy/wonder-blocks-typography";
import {useQuery} from "@khan/data-gql";
import {reload, useLocation} from "@khan/urls";

import {
    ACCOUNT_SETTINGS_QUERY,
    mapDataToResult,
} from "./account-settings-query.ts";
import BasicSettings from "./basic-settings.tsx";
import LinkedAccounts from "./linked-accounts/linked-accounts.tsx";
import MessageBanner from "./linked-accounts/message-banner.tsx";
import PasswordForm from "./password-form.tsx";
import SuccessToast from "./success-toast.tsx";

interface Props {
    targetKaid: string;
}

const AccountSettings = ({targetKaid}: Props) => {
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [accountSettingsResult] = useQuery(ACCOUNT_SETTINGS_QUERY, {
        variables: {targetKaid},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
    });
    const location = useLocation();

    const handleSuccessfulChange = () => {
        setShowSuccessToast(true);
        return reload();
    };

    if (accountSettingsResult.status === "loading") {
        return <CircularSpinner size="medium" style={styles.spinner} />;
    }

    if (accountSettingsResult.status === "error") {
        return (
            <Tagline style={styles.text}>
                {t`Oops! Looks like we don’t have data available for this user.`}
            </Tagline>
        );
    }

    if (accountSettingsResult.status === "success") {
        const mappedData = mapDataToResult({
            data: accountSettingsResult.data,
            error: undefined,
            loading: false,
        });

        const {actor, target, requestInfo, sessionHash} = mappedData;

        if (!actor || !target || !requestInfo || !sessionHash) {
            return (
                <Tagline style={styles.text}>
                    {t`Oops! Looks like we don’t have data available for this user.`}
                </Tagline>
            );
        }

        return (
            <View style={{gap: sizing.size_200}}>
                <MessageBanner
                    messageKey={location.queryParams.messageKey}
                    email={location?.state ? location.state.email : null}
                />
                <BasicSettings
                    kaid={target.id}
                    targetIsChild={target.isChild}
                    initialSettings={target.settings}
                    successfullyChangedSettings={handleSuccessfulChange}
                    includesCentrallyOwnedDistrictData={
                        target.includesCentrallyOwnedDistrictData
                    }
                />
                <PasswordForm
                    actorIdentifier={actor.identifier}
                    actorIsChild={actor.isChild}
                    targetId={target.id}
                    isClasslinkUser={target.isClasslinkUser}
                    isModifyingChild={requestInfo.isModifyingChild}
                    hasPassword={target.hasPassword}
                    requiresExistingPassword={
                        requestInfo.requiresExistingPassword
                    }
                    successfullyChangedSettings={handleSuccessfulChange}
                />

                <LinkedAccounts
                    age={target.age}
                    authEmails={target.authEmails}
                    hasPassword={target.hasPassword}
                    isModifyingChild={requestInfo.isModifyingChild}
                    identifier={actor.identifier}
                    appleId={target.appleId}
                    applePrimaryEmail={target.applePrimaryEmail}
                    googlePrimaryEmail={target.googlePrimaryEmail}
                    facebookId={target.facebookId}
                    facebookPrimaryEmail={target.facebookPrimaryEmail}
                    isClasslinkUser={target.isClasslinkUser}
                />
                {showSuccessToast && (
                    <SuccessToast onClose={() => setShowSuccessToast(false)} />
                )}
            </View>
        );
    }
};

const styles = StyleSheet.create({
    spinner: {
        alignItems: "center",
    },
    text: {
        color: semanticColor.text.primary,
    },
});

export default memo(AccountSettings);
